// Show a change the moment the database confirms it, and never before.
//
// WHAT THIS REPLACED, AND WHY
// ---------------------------
// The first version of this file was optimistic: it painted the new value the
// instant the operator chose it and took it back if the server refused. That
// was the wrong trade for this cockpit, and one day of using it showed why.
//
// An optimistic screen is only honest if a 200 from the server means the change
// happened. On 23 August it did not: every jsonb patch write in the cockpit was
// being double-encoded by postgres.js, so `updateTask` answered 200, changed
// nothing, and returned the unchanged row. The optimistic layer did exactly what
// it was built to do, held the new value, waited for the truth, and the truth
// was the old value. So a task status moved, sat there for half a second, and
// slid back. See claude/jsonb-patch-writes-silently-discarded-2026-08-23.md.
//
// The lesson is not that the layer was buggy. It is that an optimistic UI
// converts a silent backend failure into a visibly confusing one, and it does
// it precisely where an operator is least able to tell which of the two they are
// looking at. In a business where these clicks move a driver's manager and a
// task somebody is accountable for, "did that save?" is not an acceptable state
// to leave a person in.
//
// WHAT HAPPENS NOW
// ----------------
// Three separate things, and keeping them separate is the whole design:
//
//   1. THE CONTROL keeps what the operator chose, and shows that it is saving.
//      It does not snap back to the old value while the request is in flight.
//      That snap-back was the actual confusion, and it lives in the controls,
//      not here. See useSaver in components/tasks/TaskCells.tsx and the pending
//      state in the two manager controls.
//   2. NOTHING ELSE MOVES until the server has answered. The card stays in its
//      group, the counts stay as they were, the badge stays as it was.
//   3. ON CONFIRMATION this module puts the confirmed value on screen straight
//      away and HOLDS it until the reload catches up, so there is no second
//      flicker between "the server said yes" and "the board has been re-read".
//
// The cost is honest and Luyanda chose it: a second or two where the control
// says it is saving and the rest of the screen has not moved. In exchange,
// anything you see on the board is something the database has confirmed.
//
// WHERE IT IS ALLOWED, AND WHERE IT IS NOT
// ----------------------------------------
// Declared per call site, never by default. A write with no declaration behaves
// exactly as it did before this file existed.
//
//   YES  one row already on the screen, one or more of its fields, confirmed.
//
//   NO   creates. There is no row yet, and inventing one means inventing an id.
//   NO   bulk operations. The server reports per-row outcomes and some of them
//        fail; the reload is the only honest account of a partial result.
//   NO   moves, swaps, allocations, merges. The change is a re-grouping, not a
//        field, and half a tree is not a smaller lie than a slow one.
//   NO   anything that sends money or a document to an owner or a driver.
//
// web/test/confirmedWrites.test.ts holds the list of files allowed to call
// runConfirmed, so adding one is a decision somebody makes on purpose.

export type ConfirmedRow = Record<string, unknown>

export type ConfirmedSpec = {
  /**
   * True for the rows this write changed. Called on every plain object inside
   * the data of every read this spec names, so it must be cheap and specific:
   * match on an id, never on a name or a status.
   *
   * A predicate that throws is treated as "no match" rather than allowed to
   * propagate. On 23 August the task board shipped blank because one unexpected
   * value at the data boundary took the whole page down; a display convenience
   * must never be able to do that again.
   */
  match: (row: ConfirmedRow, route: string) => boolean
  /**
   * The confirmed fields to show, or a function returning them.
   *
   * Where the handler returns the updated row, build these from THAT row rather
   * than from what was requested. The difference is the whole point of this
   * module: one is what the database says, the other is what we hoped.
   *
   * The route is passed because the same fact wears different field names in
   * different reads: a car's manager is `manager_person_id` on the vehicle book
   * and `vehicle_manager_person_id` on the driver board.
   */
  set: ConfirmedRow | ((row: ConfirmedRow, route: string) => ConfirmedRow)
  /**
   * The read routes this applies to, e.g. ['tasks/getTaskBoard']. Required, and
   * deliberately so: an unscoped row match writes a car's manager into a
   * driver's manager column on any read that happens to carry both ids.
   */
  routes: string[]
  /** Shown in diagnostics only. */
  label?: string
}

type Held = {
  id: number
  spec: ConfirmedSpec
  /** Read routes whose rendered data this actually changed. */
  touched: Set<string>
  /** Routes that have come back from a read started after the confirmation. */
  fresh: Set<string>
  /** Routes whose latest data already carries the confirmed value. */
  agreed: Set<string>
  /** Sequence number at which the server confirmed. */
  confirmedSeq: number
  timer: ReturnType<typeof setTimeout> | null
}

// LET GO WHEN THE RELOAD AGREES, NOT WHEN IT ARRIVES.
//
// 23 August 2026, measured against the live cockpit. A status change was
// written and confirmed: updateTask answered with status In Progress and the
// row moved to Ongoing. The board endpoint then answered Done, the value from
// before the write, for THIRTY-FIVE SECONDS, across dozens of reads. So the
// first rule this module shipped with, "hold until a read started after the
// confirmation comes back", let go of a correct value and handed the screen
// back to a stale one. That is the pop-back Luyanda reported.
//
// The cause of the stale read is Hyperdrive query caching on the Cloudflare
// side and it is fixed there, not here. But a UI that trusts the next read to
// be newer than the last write is making an assumption it cannot check, and
// this one is cheap to check: hold until a read comes back that ALREADY
// carries the confirmed value.
//
// The ceiling is the backstop for a hold that can never be satisfied, and it is
// long on purpose: what is being held is what the server said, so holding it
// too long is a smaller error than dropping it too early. `unappliedFields` at
// the call site is what catches a server that confirmed a change it did not
// make, so nothing held here is a value the database denied.
const HOLD_CEILING_MS = 20_000

const held = new Map<number, Held>()
let nextId = 1
let version = 0
const listeners = new Set<() => void>()

// A monotonic counter, not a clock. It answers one question: did this read start
// before or after the server confirmed? A read already in flight when the write
// landed comes back with data from BEFORE the change, so treating it as the
// reload catching up is how the value flickers back to the old one for a frame.
let seq = 0

// Bumped whenever a read is accepted as new enough to judge agreement. It is
// part of the memo key below: without it, a second read carrying the identical
// object would be short-circuited and its agreement never noticed.
let evalEpoch = 0

export function nextSeq(): number {
  return ++seq
}

export function getConfirmedVersion(): number {
  return version
}

export function subscribeConfirmed(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

function announce(): void {
  version += 1
  for (const listener of [...listeners]) listener()
}

// Node has setTimeout too, and a pending timer holds the event loop open, which
// is how a server-side render of this tree hangs instead of exiting. Same edge
// as the BroadcastChannel in useBackend.ts. unref where it exists, no-op in a
// browser where it does not.
function unrefIfPossible(timer: ReturnType<typeof setTimeout>): ReturnType<typeof setTimeout> {
  const maybe = timer as unknown as { unref?: () => void }
  if (typeof maybe.unref === 'function') maybe.unref()
  return timer
}

function release(id: number): void {
  const entry = held.get(id)
  if (!entry) return
  if (entry.timer) clearTimeout(entry.timer)
  held.delete(id)
  announce()
}

/**
 * Start showing a value the server has just confirmed. Returns its id.
 *
 * Exported for the tests and for a caller that needs the two halves apart. The
 * ordinary path is runConfirmed below.
 */
export function holdConfirmed(spec: ConfirmedSpec): number {
  const id = nextId++
  const entry: Held = {
    id,
    spec,
    touched: new Set(),
    fresh: new Set(),
    agreed: new Set(),
    confirmedSeq: nextSeq(),
    timer: null,
  }
  entry.timer = unrefIfPossible(setTimeout(() => release(id), HOLD_CEILING_MS))
  held.set(id, entry)
  announce()
  return id
}

/**
 * A read finished. `startedSeq` is the counter value taken when it was FIRED,
 * which is what decides whether its data can be trusted to include the change.
 */
export function noteReadSettled(route: string, startedSeq: number): void {
  for (const entry of held.values()) {
    if (startedSeq < entry.confirmedSeq) continue
    if (!entry.spec.routes.includes(route)) continue
    // Only that the read is new enough to be worth believing. Whether it
    // actually agrees is decided in walk(), when the data is rendered.
    entry.fresh.add(route)
    entry.agreed.delete(route)
    evalEpoch += 1
  }
}

/** True when every read that was showing this value now carries it by itself. */
function satisfied(entry: Held): boolean {
  if (entry.touched.size === 0) return false
  for (const shown of entry.touched) if (!entry.agreed.has(shown)) return false
  return true
}

function isPlainObject(value: unknown): value is ConfirmedRow {
  if (value === null || typeof value !== 'object') return false
  if (Array.isArray(value)) return false
  const proto = Object.getPrototypeOf(value)
  return proto === Object.prototype || proto === null
}

// Deep enough for board rows carrying subitems carrying links. Anything deeper
// than this is not a row shape and should not be walked on every render.
const MAX_DEPTH = 8

function merge(row: ConfirmedRow, entry: Held, route: string): ConfirmedRow | null {
  let fields: ConfirmedRow
  try {
    if (!entry.spec.match(row, route)) return null
    fields = typeof entry.spec.set === 'function' ? entry.spec.set(row, route) : entry.spec.set
  } catch {
    // A broken predicate or setter costs this row its update. It does not cost
    // the operator the screen.
    return null
  }
  let differs = false
  for (const key of Object.keys(fields)) {
    if (!Object.is(row[key], fields[key])) {
      differs = true
      break
    }
  }
  // A match that needs no change is the reload agreeing. That, and only that,
  // is what lets the hold go.
  if (!differs) {
    if (entry.fresh.has(route)) entry.agreed.add(route)
    return null
  }
  return { ...row, ...fields }
}

function walk(value: unknown, active: Held[], route: string, depth: number): unknown {
  if (depth > MAX_DEPTH) return value

  if (Array.isArray(value)) {
    let changed = false
    const out = value.map((item) => {
      const next = walk(item, active, route, depth + 1)
      if (next !== item) changed = true
      return next
    })
    // Reference identity is not a nicety here. Pages hold their derived data in
    // useMemo keyed on `board.data`, so handing back a new array on every render
    // would recompute every grouping in the cockpit on every render.
    return changed ? out : value
  }

  if (!isPlainObject(value)) return value

  let out: ConfirmedRow = value
  let changed = false
  for (const key of Object.keys(value)) {
    const child = value[key]
    const next = walk(child, active, route, depth + 1)
    if (next !== child) {
      if (!changed) {
        out = { ...value }
        changed = true
      }
      out[key] = next
    }
  }

  for (const entry of active) {
    const merged = merge(out, entry, route)
    if (!merged) continue
    out = merged
    changed = true
    entry.touched.add(route)
  }

  return changed ? out : value
}

type Memo = { version: number; epoch: number; route: string; out: unknown }
const memo = new WeakMap<object, Memo>()

/**
 * Renders `data` with any confirmed change the reload has not caught up with.
 *
 * Returns the identical reference when nothing applies, which is what keeps this
 * invisible to the fifteen pages that never asked for it.
 */
export function applyConfirmed<T>(route: string, data: T): T {
  if (held.size === 0) return data
  if (data === null || typeof data !== 'object') return data

  const active: Held[] = []
  for (const entry of held.values()) {
    if (entry.spec.routes.includes(route)) active.push(entry)
  }
  if (active.length === 0) return data

  const key = data as unknown as object
  const cached = memo.get(key)
  if (cached && cached.version === version && cached.epoch === evalEpoch && cached.route === route) {
    return cached.out as T
  }

  const out = walk(data, active, route, 0)
  memo.set(key, { version, epoch: evalEpoch, route, out })

  // Releasing is a state change and this runs during render, so it is deferred
  // by a microtask rather than done here.
  for (const entry of active) {
    if (!satisfied(entry)) continue
    const id = entry.id
    queueMicrotask(() => {
      const still = held.get(id)
      if (still && satisfied(still)) release(id)
    })
  }
  return out as T
}

/**
 * Send the write, and show the result only once the server has confirmed it.
 *
 * `build` receives whatever the handler returned, so a spec can be built from
 * the database's own row rather than from what was requested. Returning null
 * means "show nothing early", which is the right answer for a handler that
 * returns no row: the reload will do it.
 */
export async function runConfirmed<T>(
  write: () => Promise<T>,
  build: (result: T) => ConfirmedSpec | null,
): Promise<T> {
  const result = await write()
  let spec: ConfirmedSpec | null = null
  try {
    spec = build(result)
  } catch {
    // Never let a display decision turn a successful write into a failed one in
    // the operator's eyes. The reload still shows the truth a moment later.
    spec = null
  }
  if (spec) holdConfirmed(spec)
  return result
}

/** Test seam. Never called by the app. */
export function __resetConfirmedForTests(): void {
  for (const entry of held.values()) if (entry.timer) clearTimeout(entry.timer)
  held.clear()
  nextId = 1
  version = 0
  seq = 0
  evalEpoch = 0
  listeners.clear()
}

/** Test seam. Never called by the app. */
export function __heldCountForTests(): number {
  return held.size
}
